import { useMemo } from "react";
import useAnalytics from "../../../packages/analytics";

const toEnumerableError = (err: unknown) =>
	JSON.parse(JSON.stringify(err, Object.getOwnPropertyNames(err)));

type Options = {
	name?: string;
	successName?: string;
	errorName?: string;
};

/**
 * Creates a wrapped version of the given callback (synchronous or asynchronous) that
 * fires analytics events upon success or error.
 *
 * @typeParam T - The type of the callback function (e.g. `(...args: any[]) => any`).
 *   If `T` is synchronous, its return value is automatically wrapped in a Promise.
 *   If `T` is already an async function, it remains asynchronous.
 * @param callback - The original callback to wrap (may be sync or async).
 * @param fireAnalytics - The analytics function returned by `useAnalytics`.
 * @param successName - The name of the success analytics event.
 * @param errorName - The name of the error analytics event.
 * @returns A wrapped async function that fires the appropriate analytics events
 *   and returns a `Promise`.
 */
export const createAnalyticsWrappedCallback = <
	T extends (...args: any[]) => any,
>(
	callback: T,
	fireAnalytics: ReturnType<typeof useAnalytics>,
	successName: string,
	errorName: string,
) => {
	return async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
		try {
			const response = await Promise.resolve(callback(...args));
			fireAnalytics(successName, { payload: args, response });
			return response;
		} catch (err) {
			fireAnalytics(errorName, toEnumerableError(err));
			throw err;
		}
	};
};

/**
 * A React hook that returns a wrapped version of the given callback (sync or async).
 * When invoked, it fires analytics events upon success or error.
 *
 * @typeParam T - The type of the callback function (e.g. `(...args: any[]) => any`).
 *   If `T` is synchronous, its return value will be wrapped in a `Promise`.
 * @param callback - A function (sync or async) whose calls should trigger analytics events.
 * @param options - Optional configuration for analytics:
 *   - `name` is the identifier passed to `useAnalytics`.
 *   - `successName` is the analytics event name for successful calls.
 *   - `errorName` is the analytics event name for error calls.
 * @returns A memoized function that always returns a `Promise<Awaited<ReturnType<T>>>`.
 *   Regardless of whether the original function is sync or async, you must `await` or
 *   handle the returned promise.
 */
export const useAnalyticsReporting = <T extends (...args: any[]) => any>(
	callback: T,
	{ name, successName = "success", errorName = "error" }: Options = {},
) => {
	const fireAnalytics = useAnalytics(name);

	return useMemo(
		() =>
			createAnalyticsWrappedCallback(
				callback,
				fireAnalytics,
				successName,
				errorName,
			),
		[callback, fireAnalytics, successName, errorName],
	);
};
